'use client'

import { useEffect, useMemo } from 'react'
import { useThree } from '@react-three/fiber'
import {
  float,
  vec3,
  smoothstep,
  positionWorld,
  fog,
  densityFogFactor,
} from 'three/tsl'

/**
 * Height-based fog using Three.js scene-level fog nodes (TSL)
 * Hooks into the built-in fog system — applies to all materials with fog: true
 * No mesh needed — fog is calculated per-fragment using world position
 *
 * @param {Array} color - RGB color [r, g, b] values 0-1
 * @param {number} density - Exponential fog density (higher = thicker)
 * @param {number} minHeight - Y position where fog is at full strength
 * @param {number} maxHeight - Y position where fog fades to zero
 */
export function HeightFog({
  color = [0.85, 0.9, 0.95],
  density = 0.02,
  minHeight = 0,
  maxHeight = 80,
}) {
  const scene = useThree((state) => state.scene)

  // Destructure to primitives so deps are stable across renders
  // (passing a new array literal each render would cause the fogNode and
  // every dependent material's shader to rebuild — extremely expensive)
  const r = color[0]
  const g = color[1]
  const b = color[2]

  const fogNode = useMemo(() => {
    const fogColor = vec3(r, g, b)

    // Height-based falloff: full fog below minHeight, none above maxHeight
    const heightFactor = float(1.0).sub(
      smoothstep(float(minHeight), float(maxHeight), positionWorld.y)
    )

    // Combine distance-based density fog with height falloff
    const combinedFactor = densityFogFactor(float(density)).mul(heightFactor)

    return fog(fogColor, combinedFactor)
  }, [r, g, b, density, minHeight, maxHeight])

  useEffect(() => {
    // Object.assign bypasses React Compiler's immutability check on the scene ref
    Object.assign(scene, { fogNode })
    return () => {
      Object.assign(scene, { fogNode: null })
    }
  }, [scene, fogNode])

  return null
}

export default HeightFog
